/**
 * Nymia · Admin — Meta CAPI Diagnostic Card
 * ──────────────────────────────────────────
 * Phase 3 Compliance audit (Feb 2026).
 *
 * Consolidates in a single card everything needed to certify the
 * Meta Pixel + Conversions API tracking pipeline before launch:
 *   1. `/api/admin/meta/history` — mock-vs-real usage history.
 *   2. `/api/admin/meta/verify-credentials` — Graph API ping on the
 *      configured pixel_id + access_token pair.
 *   3. `/api/admin/meta/diagnostic/run` — fires a battery of standard
 *      Meta events (PageView, ViewContent, Lead, CompleteRegistration,
 *      InitiateCheckout, Purchase) with the configured test_event_code
 *      so the admin can verify reception in Meta Events Manager
 *      → Test Events.
 *   4. `/api/admin/meta/recent-events` — latest 20 log rows with
 *      request/response payload for audit trail.
 */
import React, { useEffect, useState } from 'react';
import { Activity, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Play, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import api from '../../api/client';
import { Button } from '../ui/button';

const StatusPill = ({ ok, warn, label, testId }) => {
  const bg = ok
    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
    : warn
      ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
      : 'bg-red-500/15 text-red-300 border-red-500/30';
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${bg}`}
      data-testid={testId}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
};

const Field = ({ label, value, mono = false, testId }) => (
  <div className="flex items-center justify-between gap-4 py-1.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span
      className={`text-xs ${mono ? 'font-mono' : ''} text-foreground/90`}
      data-testid={testId}
    >
      {value === null || value === undefined || value === '' ? '—' : String(value)}
    </span>
  </div>
);

const EventResultRow = ({ result }) => {
  const [open, setOpen] = useState(false);
  const ok = !!result.ok;
  return (
    <div className="rounded-md border border-border/60 bg-background/40" data-testid={`diag-result-${result.event_name}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="min-w-[8rem] font-mono text-xs font-semibold">{result.event_name}</span>
        <StatusPill ok={ok} label={ok ? `HTTP ${result.http_status}` : `HTTP ${result.http_status || 'ERR'}`} testId={`diag-pill-${result.event_name}`} />
        {result.events_received !== undefined && result.events_received !== null && (
          <span className="text-[11px] text-muted-foreground">events_received: <span className="font-mono text-emerald-300">{result.events_received}</span></span>
        )}
        {result.fbtrace_id && (
          <span className="ml-auto text-[10px] font-mono text-muted-foreground/70">fbtrace {result.fbtrace_id.slice(0, 10)}…</span>
        )}
      </button>
      {open && (
        <div className="grid gap-3 border-t border-border/40 px-3 py-3 md:grid-cols-2">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Request payload</p>
            <pre className="max-h-56 overflow-auto rounded bg-black/60 p-2 text-[10px] leading-tight text-emerald-200">{JSON.stringify(result.request_payload, null, 2)}</pre>
          </div>
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Meta response</p>
            <pre className="max-h-56 overflow-auto rounded bg-black/60 p-2 text-[10px] leading-tight text-amber-200">{JSON.stringify(result.response, null, 2)}</pre>
            {result.error && (
              <p className="mt-2 rounded bg-red-500/10 px-2 py-1 text-[11px] text-red-300">{result.error}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RecentEventRow = ({ ev }) => {
  const [open, setOpen] = useState(false);
  const mock = !!ev.mock;
  return (
    <div className="rounded-md border border-border/40 bg-background/40" data-testid="meta-recent-row">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left"
      >
        {open ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
        <span className="min-w-[10rem] font-mono text-[11px]">{ev.event_name}</span>
        {mock ? (
          <StatusPill warn label="mock" testId="row-pill-mock" />
        ) : ev.delivered ? (
          <StatusPill ok label={`HTTP ${ev.http_status || 200}`} testId="row-pill-delivered" />
        ) : (
          <StatusPill label={`HTTP ${ev.http_status || 'ERR'}`} testId="row-pill-failed" />
        )}
        {ev.event_id && <span className="text-[10px] font-mono text-muted-foreground/70">{ev.event_id.slice(0, 24)}…</span>}
        <span className="ml-auto text-[10px] text-muted-foreground/70">{new Date(ev.created_at).toLocaleString()}</span>
      </button>
      {open && (
        <div className="border-t border-border/30 px-3 py-2 text-[10px]">
          <div className="grid gap-2 md:grid-cols-2">
            <div>
              <p className="mb-1 uppercase tracking-widest text-muted-foreground">Request</p>
              <pre className="max-h-40 overflow-auto rounded bg-black/60 p-2 leading-tight text-emerald-200">{JSON.stringify(ev.request_payload || ev, null, 2)}</pre>
            </div>
            <div>
              <p className="mb-1 uppercase tracking-widest text-muted-foreground">Response</p>
              <pre className="max-h-40 overflow-auto rounded bg-black/60 p-2 leading-tight text-amber-200">{JSON.stringify(ev.response || { note: mock ? 'mock — no HTTP call' : null }, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MetaDiagnosticCard = () => {
  const [history, setHistory] = useState(null);
  const [verify, setVerify] = useState(null);
  const [recent, setRecent] = useState([]);
  const [diagnostic, setDiagnostic] = useState(null);
  const [matchQuality, setMatchQuality] = useState(null);
  const [loading, setLoading] = useState({ history: false, verify: false, recent: false, diagnostic: false, mq: false });
  const [err, setErr] = useState('');

  const loadHistory = async () => {
    setLoading((l) => ({ ...l, history: true }));
    try {
      const { data } = await api.get('/admin/meta/history');
      setHistory(data);
    } catch (e) {
      setErr(`History: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading((l) => ({ ...l, history: false }));
    }
  };
  const loadRecent = async () => {
    setLoading((l) => ({ ...l, recent: true }));
    try {
      const { data } = await api.get('/admin/meta/recent-events?limit=20');
      setRecent(data || []);
    } catch (e) {
      setErr(`Recent: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading((l) => ({ ...l, recent: false }));
    }
  };
  const loadMatchQuality = async () => {
    setLoading((l) => ({ ...l, mq: true }));
    try {
      const { data } = await api.get('/admin/meta/match-quality?sample=200');
      setMatchQuality(data);
    } catch (e) {
      setErr(`Match quality: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading((l) => ({ ...l, mq: false }));
    }
  };
  const runVerify = async () => {
    setLoading((l) => ({ ...l, verify: true }));
    setErr('');
    try {
      const { data } = await api.get('/admin/meta/verify-credentials');
      setVerify(data);
    } catch (e) {
      setErr(`Verify: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading((l) => ({ ...l, verify: false }));
    }
  };
  const runDiagnostic = async () => {
    setLoading((l) => ({ ...l, diagnostic: true }));
    setErr('');
    try {
      const { data } = await api.post('/admin/meta/diagnostic/run', {});
      setDiagnostic(data);
      // Refresh history, recent & match quality after a diagnostic run
      await Promise.all([loadHistory(), loadRecent(), loadMatchQuality()]);
    } catch (e) {
      setErr(`Diagnostic: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading((l) => ({ ...l, diagnostic: false }));
    }
  };

  useEffect(() => {
    loadHistory();
    loadRecent();
    loadMatchQuality();
  }, []);

  const cfg = history?.current_config || {};
  const tot = history?.totals || {};
  const mockActive = !!cfg.meta_mock_mode;
  const pixelSet = !!cfg.pixel_id_set;
  const tokenSet = !!cfg.access_token_set;
  const tokenLength = cfg.access_token_length || 0;
  const tokenSuspicious = tokenSet && tokenLength < 100;
  const testCodeSet = !!cfg.test_event_code;

  return (
    <div
      className="rounded-lg border border-border/60 bg-card/40 p-4 md:p-5"
      data-testid="meta-diagnostic-card"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 gold-text" />
          <h3 className="text-sm font-semibold uppercase tracking-widest">Meta CAPI · Launch Certification</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { loadHistory(); loadRecent(); }}
          disabled={loading.history}
          data-testid="meta-refresh-btn"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading.history ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {err && (
        <p className="mb-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="meta-diag-error">
          {err}
        </p>
      )}

      {/* ═══════ 1. Configurazione corrente ═══════ */}
      <div className="mb-4 rounded-md border border-border/50 bg-background/30 p-3">
        <p className="mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">1 · Configurazione corrente</p>
        <div className="flex flex-wrap items-center gap-2">
          {mockActive ? (
            <StatusPill warn label="MOCK MODE ATTIVO — nessun evento inviato realmente" testId="meta-cfg-mock" />
          ) : (
            <StatusPill ok label="Mock mode OFF · invii reali attivi" testId="meta-cfg-live" />
          )}
          {pixelSet ? (
            <StatusPill ok label={`Pixel ID: ${cfg.pixel_id_preview}`} testId="meta-cfg-pixel" />
          ) : (
            <StatusPill label="Pixel ID mancante" testId="meta-cfg-pixel-missing" />
          )}
          {tokenSet ? (
            <StatusPill
              ok={!tokenSuspicious}
              warn={tokenSuspicious}
              label={`Token: ${cfg.access_token_masked}${tokenSuspicious ? ` (${tokenLength} char — troppo corto)` : ''}`}
              testId="meta-cfg-token"
            />
          ) : (
            <StatusPill label="Access token mancante" testId="meta-cfg-token-missing" />
          )}
          {testCodeSet ? (
            <StatusPill ok label={`Test code: ${cfg.test_event_code}`} testId="meta-cfg-testcode" />
          ) : (
            <StatusPill warn label="Test code non impostato" testId="meta-cfg-testcode-missing" />
          )}
        </div>
        {tokenSuspicious && (
          <p className="mt-2 rounded bg-amber-500/10 px-2 py-1 text-[11px] text-amber-200">
            ⚠ L&apos;access token ha solo {tokenLength} caratteri. I System User Access Token di Meta sono lunghi ~200 caratteri.
            Genera un nuovo token: <a className="underline" href="https://business.facebook.com/settings/system-users" target="_blank" rel="noopener noreferrer">Business Manager → System Users → Generate New Token</a>
            {' '}con permesso <code className="rounded bg-black/40 px-1">ads_management</code>.
          </p>
        )}
      </div>

      {/* ═══════ 2. Storico invii ═══════ */}
      <div className="mb-4 rounded-md border border-border/50 bg-background/30 p-3">
        <p className="mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">2 · Storico invii (audit)</p>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <div className="rounded bg-background/40 p-2">
            <p className="text-[10px] text-muted-foreground">Totale log</p>
            <p className="mt-0.5 font-mono text-sm" data-testid="meta-total-log">{tot.events_log_total ?? '—'}</p>
          </div>
          <div className="rounded bg-background/40 p-2">
            <p className="text-[10px] text-muted-foreground">Eventi mock</p>
            <p className="mt-0.5 font-mono text-sm text-amber-300" data-testid="meta-mock-count">{tot.mock_events ?? '—'}</p>
          </div>
          <div className="rounded bg-background/40 p-2">
            <p className="text-[10px] text-muted-foreground">Consegnati HTTP 200</p>
            <p className="mt-0.5 font-mono text-sm text-emerald-300" data-testid="meta-delivered-count">{tot.real_delivered_200 ?? '—'}</p>
          </div>
          <div className="rounded bg-background/40 p-2">
            <p className="text-[10px] text-muted-foreground">Errori HTTP</p>
            <p className="mt-0.5 font-mono text-sm text-red-300" data-testid="meta-failed-count">{tot.real_failed ?? '—'}</p>
          </div>
        </div>
        {tot.real_attempted === 0 && tot.mock_events > 0 && (
          <p className="mt-2 rounded bg-amber-500/10 px-2 py-1 text-[11px] text-amber-200" data-testid="meta-history-warning">
            ⚠ Nessun evento reale è mai stato inviato a Meta in questo ambiente. Tutti i {tot.mock_events} eventi log sono mock.
          </p>
        )}
        {history?.first_real_event && (
          <div className="mt-2 space-y-0.5">
            <Field label="Primo evento reale" value={new Date(history.first_real_event.created_at).toLocaleString()} testId="meta-first-real" />
            <Field label="Ultimo evento reale" value={new Date(history.last_real_event.created_at).toLocaleString()} testId="meta-last-real" />
            <Field label="Eventi consegnati" value={(history.event_names_delivered || []).join(', ') || '—'} testId="meta-event-names" />
          </div>
        )}
      </div>

      {/* ═══════ 3. Verifica credenziali ═══════ */}
      <div className="mb-4 rounded-md border border-border/50 bg-background/30 p-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">3 · Verifica credenziali (Graph API ping)</p>
          <Button
            size="sm"
            variant="secondary"
            onClick={runVerify}
            disabled={loading.verify}
            data-testid="meta-verify-btn"
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            {loading.verify ? 'Verifica…' : 'Verifica ora'}
          </Button>
        </div>
        {verify && (
          <div className="rounded bg-background/40 p-2 text-[11px]">
            {verify.ok ? (
              <div className="text-emerald-300">
                ✅ HTTP {verify.http_status} · Pixel: <span className="font-mono">{verify.pixel_name || verify.pixel_id}</span>
              </div>
            ) : (
              <>
                <div className="text-red-300">
                  ❌ HTTP {verify.http_status || 'error'} · {verify.response?.error?.message || verify.network_error || verify.reason || 'Errore sconosciuto'}
                </div>
                {verify.response?.error?.fbtrace_id && (
                  <div className="mt-1 font-mono text-[10px] text-muted-foreground/70">fbtrace_id: {verify.response.error.fbtrace_id}</div>
                )}
              </>
            )}
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-black/60 p-2 text-[10px] leading-tight text-muted-foreground/90" data-testid="meta-verify-response">{JSON.stringify(verify, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* ═══════ 4. Full diagnostic ═══════ */}
      <div className="mb-4 rounded-md border border-border/50 bg-background/30 p-3">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">4 · Full diagnostic (batch 6 eventi)</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Spara: PageView, ViewContent, Lead, CompleteRegistration, InitiateCheckout, Purchase
              con test_event_code — verifica in Meta Events Manager → Test Events.
            </p>
          </div>
          <Button
            size="sm"
            onClick={runDiagnostic}
            disabled={loading.diagnostic || mockActive}
            className="gold-gradient text-black hover:opacity-90 disabled:opacity-40"
            data-testid="meta-diagnostic-run-btn"
          >
            <Play className="mr-1.5 h-3.5 w-3.5" />
            {loading.diagnostic ? 'In corso…' : 'Run full diagnostic'}
          </Button>
        </div>
        {mockActive && (
          <p className="mt-2 rounded bg-amber-500/10 px-2 py-1 text-[11px] text-amber-200">
            ⚠ Disattiva prima <strong>mock mode</strong> nelle impostazioni Meta sopra e salva, poi torna qui.
          </p>
        )}
        {diagnostic && (
          <div className="mt-3 space-y-2" data-testid="meta-diagnostic-results">
            <div className="mb-2 flex items-center gap-3 text-[11px]">
              {diagnostic.ok ? (
                <StatusPill ok label={`Tutti i ${diagnostic.events_delivered}/${diagnostic.events_tested} eventi consegnati`} testId="meta-diag-summary" />
              ) : (
                <StatusPill label={`${diagnostic.events_delivered}/${diagnostic.events_tested} consegnati`} testId="meta-diag-summary-fail" />
              )}
              {diagnostic.test_event_code_used && <span className="text-muted-foreground">Test event code propagato ✓</span>}
            </div>
            <div className="space-y-2">
              {diagnostic.results.map((r) => (
                <EventResultRow key={r.event_name} result={r} />
              ))}
            </div>
            <p className="mt-2 rounded bg-emerald-500/5 px-2 py-1 text-[11px] text-muted-foreground">
              Ora apri <a className="underline" href="https://business.facebook.com/events_manager2/list/dataset" target="_blank" rel="noopener noreferrer">Meta Events Manager</a> → <strong>Test Events</strong> e verifica che gli eventi appaiano con il codice <code className="rounded bg-black/40 px-1">{cfg.test_event_code || '…'}</code>.
            </p>
          </div>
        )}
      </div>

      {/* ═══════ 5. Event Match Quality ═══════ */}
      <div className="mb-4 rounded-md border border-border/50 bg-background/30 p-3" data-testid="meta-mq-panel">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">5 · Event Match Quality (user_data coverage)</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Percentuale di eventi Meta CAPI arricchiti con ogni user_data field. Più alto = Match Quality migliore (Meta considera ≥7/10 come &quot;Good&quot;).
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={loadMatchQuality} disabled={loading.mq}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading.mq ? 'animate-spin' : ''}`} />
            Ricarica
          </Button>
        </div>
        {matchQuality && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Sample: <strong className="font-mono">{matchQuality.sample_size}</strong> eventi</span>
              {matchQuality.emq_estimate !== null && (
                <StatusPill
                  ok={matchQuality.emq_estimate >= 7}
                  warn={matchQuality.emq_estimate >= 5 && matchQuality.emq_estimate < 7}
                  label={`EMQ stimato: ${matchQuality.emq_estimate}/10`}
                  testId="meta-emq-score"
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {['user_email_hashed', 'external_id_provided', 'ip_provided', 'ua_provided', 'fbp_provided', 'fbc_provided'].map((f) => {
                const c = matchQuality.coverage?.[f];
                if (!c) return null;
                const label = {
                  user_email_hashed: 'Email hashed (em)',
                  external_id_provided: 'External ID',
                  ip_provided: 'Client IP',
                  ua_provided: 'User Agent',
                  fbp_provided: 'fbp cookie',
                  fbc_provided: 'fbc cookie',
                }[f];
                const good = c.pct >= 80;
                const warn = c.pct >= 40 && c.pct < 80;
                return (
                  <div key={f} className="rounded bg-background/40 p-2" data-testid={`meta-mq-${f}`}>
                    <p className="text-[10px] text-muted-foreground">{label}</p>
                    <p className={`mt-0.5 font-mono text-sm ${good ? 'text-emerald-300' : warn ? 'text-amber-300' : 'text-red-300'}`}>
                      {c.pct}% <span className="text-[10px] text-muted-foreground/70">({c.count})</span>
                    </p>
                  </div>
                );
              })}
            </div>
            {matchQuality.coverage?.fbc_provided?.pct < 20 && (
              <p className="mt-2 rounded bg-amber-500/10 px-2 py-1 text-[11px] text-amber-200">
                ⓘ <strong>fbc bassa è normale</strong>: il cookie <code className="rounded bg-black/40 px-1">_fbc</code> viene settato SOLO quando l&apos;utente arriva da una campagna Meta con <code>fbclid</code> nell&apos;URL. Utenti diretti / SEO non hanno fbc.
              </p>
            )}
            {matchQuality.coverage?.fbp_provided?.pct < 50 && (
              <p className="mt-2 rounded bg-red-500/10 px-2 py-1 text-[11px] text-red-200">
                ⚠ <strong>fbp coverage bassa</strong>: verifica che il Meta Pixel browser sia caricato SEMPRE prima delle interazioni utente. Cause: cookie consent negato, ad blocker attivo, Pixel non caricato per errore init.
              </p>
            )}
          </>
        )}
      </div>

      {/* ═══════ 6. Ultimi eventi ═══════ */}
      <div className="rounded-md border border-border/50 bg-background/30 p-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">6 · Ultimi 20 eventi (log completo)</p>
          <Button variant="ghost" size="sm" onClick={loadRecent} disabled={loading.recent}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading.recent ? 'animate-spin' : ''}`} />
            Ricarica
          </Button>
        </div>
        <div className="space-y-1.5" data-testid="meta-recent-list">
          {recent.length === 0 && <p className="text-[11px] text-muted-foreground">Nessun evento registrato.</p>}
          {recent.map((ev, i) => (
            <RecentEventRow key={ev.event_id ? `${ev.event_id}-${i}` : `ev-${i}`} ev={ev} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MetaDiagnosticCard;
